// IMPORTANT:
// this is a stripped down version of @mantine/core Select component, which we couldn't use directly
// for infinite select, because it doesn't allow programmatical closing of the custom dropdown ¯\_(ツ)_/¯

import React, { useState, useEffect, useRef } from 'react';
import {
  useDebouncedValue,
  useDidUpdate,
  useUncontrolled,
} from '@mantine/hooks';
import {
  CloseButton,
  Input,
  Popover,
  SelectProps,
  getDefaultZIndex,
  rem,
} from '@mantine/core';

import useStyles from './InfiniteSelect.styles';
import { InfiniteListItem } from 'components/InfiniteList/InfiniteList';
import { identity } from 'helpers/miscUtils';

export type InfiniteDropdownProps = {
  query?: string;
  hovered?: number;
  nothingFound?: React.ReactNode;
  itemComponent?: React.ComponentType<any>;
  onItemHover?: (index: number) => void;
  onItemSelect?: (item: InfiniteListItem<any>) => void;
};

interface InfiniteSelectProps<TValue = string>
  extends Omit<
    SelectProps,
    'data' | 'onChange' | 'value' | 'defaultValue' | 'onSearchChange'
  > {
  value?: InfiniteListItem<TValue> | null;
  defaultValue?: InfiniteListItem<TValue> | null;
  onChange?: (value: InfiniteListItem<TValue> | null) => void;
  onSearchChange?: (value: string) => void;
  dropdownComponent?: React.ComponentType<InfiniteDropdownProps>;
  itemToLabel?: (item: TValue) => string;
  itemToValue?: (item: TValue) => string;
  nothingFound?: React.ReactNode;
  hoverOnSearchChange?: boolean;
  initiallyOpened?: boolean;
  readOnly?: boolean;
  withinPortal?: boolean;
  zIndex?: string | number;
  minDropdownWidth?: number;
}

export function InfiniteSelect<TValue = string>(
  props: InfiniteSelectProps<TValue>
) {
  const {
    // Input.Wrapper props
    label,
    description,
    error,
    required,
    withAsterisk,
    labelProps,
    descriptionProps,
    errorProps,
    inputWrapperOrder,
    inputContainer,
    className,
    style,
    id,
    // Input props
    placeholder,
    size = 'sm',
    variant,
    radius,
    leftSection,
    rightSection,
    disabled = false,
    name,
    form,
    // behavior props
    value,
    defaultValue,
    onChange,
    onSearchChange,
    searchValue,
    searchable = false,
    clearable = false,
    allowDeselect,
    readOnly = false,
    nothingFound,
    maxDropdownHeight = 220,
    minDropdownWidth = 220,
    withinPortal = true,
    zIndex = getDefaultZIndex('popover'),
    initiallyOpened = false,
    onBlur,
    onFocus,
    onDropdownOpen,
    onDropdownClose,
    hoverOnSearchChange,
    itemToLabel = identity as (item: TValue) => string,
    dropdownComponent: Dropdown,
  } = props;

  const itemToValue = props.itemToValue ?? itemToLabel;
  const { classes, cx } = useStyles();
  const [dropdownOpened, _setDropdownOpened] = useState(initiallyOpened);
  const [hovered, setHovered] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isDeselectable =
    allowDeselect === undefined ? clearable : allowDeselect;

  const [_value, handleChange] = useUncontrolled<InfiniteListItem<TValue> | null>(
    {
      value,
      defaultValue,
      finalValue: null,
      onChange,
    }
  );

  const [inputValue, setInputValue] = useUncontrolled<string>({
    value: searchValue,
    defaultValue: _value?.label || '',
    finalValue: '',
    onChange: onSearchChange,
  });
  const [debouncedInputValue] = useDebouncedValue(inputValue, 500);

  const setDropdownOpened = (opened: boolean) => {
    if (dropdownOpened !== opened) {
      _setDropdownOpened(opened);
      const handler = opened ? onDropdownOpen : onDropdownClose;
      typeof handler === 'function' && handler();
    }
  };

  const handleSearchChange = (val: string) => {
    setInputValue(val);
  };

  const handleClear = () => {
    if (!readOnly) {
      handleChange(null);
      handleSearchChange('');
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (_value && (!searchable || !dropdownOpened)) {
      handleSearchChange(itemToLabel(_value.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_value]);

  const handleItemSelect = (item: InfiniteListItem<TValue>) => {
    if (!readOnly) {
      if (
        isDeselectable &&
        value?.value &&
        itemToValue(value.value) === itemToValue(item.value)
      ) {
        handleChange(null);
        setDropdownOpened(false);
      } else {
        handleChange(item);
        setHovered(-1);
        setDropdownOpened(false);
        inputRef.current?.focus();
      }
    }
  };

  useDidUpdate(() => {
    setHovered(hoverOnSearchChange ? 0 : -1);
  }, [hoverOnSearchChange]);

  const shouldShowDropdown = !readOnly && dropdownOpened;

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    typeof onBlur === 'function' && onBlur(event);
    setDropdownOpened(false);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    typeof onFocus === 'function' && onFocus(event);
    if (searchable) {
      setDropdownOpened(true);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!readOnly) {
      handleSearchChange(event.currentTarget.value);

      if (clearable && event.currentTarget.value === '') {
        handleChange(null);
      }

      setHovered(-1);
      setDropdownOpened(true);
    }
  };

  const handleInputClick = () => {
    if (!readOnly) {
      setDropdownOpened(!dropdownOpened);
    }
  };

  const rightSectionContent =
    clearable && _value ? (
      <CloseButton
        size={size}
        onMouseDown={(event) => event.preventDefault()}
        onClick={handleClear}
        disabled={disabled}
        style={{ pointerEvents: 'all', background: 'var(--input-bg)' }}
      />
    ) : (
      rightSection
    );

  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      errorProps={errorProps}
      inputWrapperOrder={inputWrapperOrder}
      inputContainer={inputContainer}
      id={id}
      className={className}
      style={style}
      size={size}>
      <Popover
        opened={shouldShowDropdown}
        withinPortal={withinPortal}
        zIndex={zIndex}
        shadow="sm"
        position="bottom-start"
        middlewares={{
          size: {
            apply({ rects, availableWidth, availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                width: `${Math.max(rects.reference.width, minDropdownWidth)}px`,
                maxWidth: `${availableWidth}px`,
                maxHeight: `${availableHeight}px`,
              });
            },
          },
        }}
        transitionProps={{ duration: 0 }}>
        <Popover.Target>
          <div onMouseLeave={() => setHovered(-1)} tabIndex={-1}>
            <input
              type="hidden"
              name={name}
              value={_value?.value ? itemToValue(_value.value) : ''}
              form={form}
              disabled={disabled}
            />

            <Input
              ref={inputRef}
              autoComplete="off"
              type="search"
              size={size}
              variant={variant}
              radius={radius}
              leftSection={leftSection}
              rightSection={rightSectionContent}
              value={inputValue}
              placeholder={placeholder}
              error={!!error}
              pointer={!searchable}
              disabled={disabled}
              onChange={handleInputChange}
              onMouseDown={handleInputClick}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              readOnly={!searchable || readOnly}
              data-mantine-stop-propagation={shouldShowDropdown}
              className={cx({ [classes.input]: !searchable })}
            />
          </div>
        </Popover.Target>

        <Popover.Dropdown
          p={0}
          onMouseDown={(event) => event.preventDefault()}>
          <div
            className={classes.dropdown}
            style={{
              maxHeight: rem(maxDropdownHeight),
              display: 'flex',
              width: '100%',
            }}>
            {Dropdown && (
              <Dropdown
                key={debouncedInputValue ?? '__all'}
                hovered={hovered}
                onItemHover={setHovered}
                onItemSelect={
                  handleItemSelect as (item: InfiniteListItem<any>) => void
                }
                nothingFound={nothingFound}
                query={
                  debouncedInputValue !== _value?.label
                    ? debouncedInputValue
                    : ''
                }
              />
            )}
          </div>
        </Popover.Dropdown>
      </Popover>
    </Input.Wrapper>
  );
}
